import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  Stack,
  Button,
  CircularProgress,
  Alert,
  FormControl,
  FormLabel,
  Input,
  Select,
  Option,
  Box,
  Typography,
} from "@mui/joy";
import {
  useEligibleWorkspacesForCopy,
  useCopyDocumentType,
  useValidateCopyName,
} from "../hooks/useDocumentTypes";
import { EligibleWorkspace } from "../types/eligibleWorkspace";
import { CopyDocumentTypeRequest } from "../types/copyDocumentTypeRequest";
import NameConflictModal from "./NameConflictModal";

interface CopyDocumentTypeDialogProps {
  open: boolean;
  documentTypeId: number;
  documentTypeName?: string;
  documentTypeDescription?: string;
  sourceWorkspaceId: string;
  sourceWorkspaceName?: string;
  onClose: () => void;
  onSuccess?: (message: string) => void;
}

const CopyDocumentTypeDialog: React.FC<CopyDocumentTypeDialogProps> = ({
  open,
  documentTypeId,
  documentTypeName,
  documentTypeDescription,
  sourceWorkspaceId,
  sourceWorkspaceName,
  onClose,
  onSuccess,
}) => {
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string>("");
  const [newName, setNewName] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  // Dialog flow states
  const [showNameConflict, setShowNameConflict] = useState(false);
  const [resolvedName, setResolvedName] = useState<string>("");
  const [originalName, setOriginalName] = useState<string>("");

  const {
    getEligibleWorkspacesForCopy,
    loading: loadingWorkspaces,
    error: workspacesError,
  } = useEligibleWorkspacesForCopy();
  const {
    copyDocumentType,
    loading: copying,
    error: copyError,
  } = useCopyDocumentType();
  const { validateCopyName, loading: validatingName } = useValidateCopyName();

  const [eligibleWorkspaces, setEligibleWorkspaces] = useState<
    EligibleWorkspace[]
  >([]);

  // Fetch eligible workspaces when dialog opens
  useEffect(() => {
    if (open) {
      setError(null);
      setNewName("");
      setShowNameConflict(false);
      setResolvedName("");
      setOriginalName("");

      const fetchWorkspaces = async () => {
        try {
          const workspaces =
            await getEligibleWorkspacesForCopy(sourceWorkspaceId);

          // Add source workspace at the beginning with "(This workspace)" label
          const sourceWorkspace: EligibleWorkspace = {
            id: sourceWorkspaceId,
            name: sourceWorkspaceName
              ? `${sourceWorkspaceName} (This workspace)`
              : "This workspace",
          };

          setEligibleWorkspaces([sourceWorkspace, ...workspaces]);
          // Pre-select the source workspace
          setSelectedWorkspaceId(sourceWorkspaceId);
        } catch (err: any) {
          if (err.status === 403) {
            setError("You do not have permission to access this endpoint.");
          } else {
            setError("Failed to load eligible workspaces. Please try again.");
          }
        }
      };

      fetchWorkspaces();
    }
  }, [
    open,
    sourceWorkspaceId,
    sourceWorkspaceName,
    getEligibleWorkspacesForCopy,
  ]);

  const executeCopy = async (nameToUse?: string) => {
    try {
      const request: CopyDocumentTypeRequest = {
        target_workspace_id: selectedWorkspaceId,
        new_name: nameToUse || newName.trim() || undefined,
      };

      await copyDocumentType(documentTypeId, request, sourceWorkspaceId);

      // Build success message
      const targetWorkspace = eligibleWorkspaces.find(
        (w) => w.id === selectedWorkspaceId,
      );
      const targetName =
        targetWorkspace?.name.replace(" (This workspace)", "") || "workspace";
      const successMessage = `Document type copied to ${targetName}`;

      // Close dialog and notify parent with success message
      onClose();
      onSuccess?.(successMessage);
    } catch (err: any) {
      const errorObj = err.errorObj || copyError;
      if (errorObj) {
        setError(errorObj.message);
      } else {
        setError(
          "An error occurred while copying the document type. Please try again.",
        );
      }
    }
  };

  const handleCopy = async () => {
    if (!selectedWorkspaceId) {
      setError("Please select a target workspace.");
      return;
    }

    setError(null);

    try {
      // Validate the name first
      const validationResult = await validateCopyName(
        documentTypeId,
        sourceWorkspaceId,
        selectedWorkspaceId,
        newName.trim() || undefined,
      );

      // If name is occupied, show name conflict modal
      if (validationResult.is_name_occupied) {
        setOriginalName(validationResult.source_document_type_name);
        setResolvedName(validationResult.resolved_name);
        setShowNameConflict(true);
      } else {
        // Otherwise, proceed with copy directly
        await executeCopy();
      }
    } catch (err: any) {
      setError(
        err.message || "Error validating document type name. Please try again.",
      );
    }
  };

  const handleNameConflictProceed = async () => {
    setShowNameConflict(false);
    await executeCopy(resolvedName);
  };

  const handleNameConflictGoBack = () => {
    setShowNameConflict(false);
    setResolvedName("");
    setOriginalName("");
  };

  const handleClose = () => {
    if (!copying && !validatingName) {
      setError(null);
      setSelectedWorkspaceId("");
      setNewName("");
      setShowNameConflict(false);
      setResolvedName("");
      setOriginalName("");
      onClose();
    }
  };

  return (
    <>
      <Modal open={open && !showNameConflict} onClose={handleClose}>
        <ModalDialog sx={{ maxWidth: 400, width: "90vw" }}>
          <DialogTitle>Copy Document Type</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              {documentTypeName && (
                <Box>
                  <Typography level="body-sm" sx={{ fontWeight: "bold" }}>
                    {documentTypeName}
                  </Typography>
                  {documentTypeDescription && (
                    <Typography
                      level="body-xs"
                      sx={{
                        fontStyle: "italic",
                        color: "text.secondary",
                        mt: 0.5,
                      }}
                    >
                      {documentTypeDescription}
                    </Typography>
                  )}
                </Box>
              )}

              {loadingWorkspaces && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <CircularProgress size="sm" />
                </Box>
              )}

              {workspacesError && (
                <Alert color="danger" size="sm">
                  {workspacesError}
                </Alert>
              )}

              {error && (
                <Alert color="danger" size="sm">
                  {error}
                </Alert>
              )}

              {copyError && !error && (
                <Alert color="danger" size="sm">
                  {typeof copyError === "string"
                    ? copyError
                    : (copyError as any).message || "An error occurred."}
                </Alert>
              )}

              {!loadingWorkspaces && (
                <>
                  <FormControl size="sm">
                    <FormLabel>Target Workspace</FormLabel>
                    <Select
                      value={selectedWorkspaceId}
                      onChange={(_, value) =>
                        setSelectedWorkspaceId(value || "")
                      }
                      placeholder="Select a workspace"
                      disabled={copying || validatingName}
                      size="sm"
                    >
                      {eligibleWorkspaces.map((workspace) => (
                        <Option key={workspace.id} value={workspace.id}>
                          {workspace.name}
                        </Option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl size="sm">
                    <FormLabel>New name (optional)</FormLabel>
                    <Input
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      placeholder="Leave empty to use original name"
                      disabled={copying || validatingName}
                      size="sm"
                    />
                  </FormControl>
                </>
              )}
            </Stack>
          </DialogContent>

          <Stack
            direction="row"
            spacing={1}
            sx={{ mt: 2, justifyContent: "flex-end" }}
          >
            <Button
              variant="outlined"
              color="neutral"
              onClick={handleClose}
              disabled={copying || loadingWorkspaces || validatingName}
              size="sm"
            >
              Cancel
            </Button>
            <Button
              variant="solid"
              color="primary"
              onClick={handleCopy}
              disabled={
                copying ||
                loadingWorkspaces ||
                validatingName ||
                !selectedWorkspaceId
              }
              loading={copying || validatingName}
              size="sm"
            >
              Copy
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>

      {/* Name Conflict Modal */}
      <NameConflictModal
        open={showNameConflict}
        originalName={originalName}
        resolvedName={resolvedName}
        onProceed={handleNameConflictProceed}
        onGoBack={handleNameConflictGoBack}
      />
    </>
  );
};

export default CopyDocumentTypeDialog;
